import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, Trash2, Globe } from 'lucide-react';
import {
  Button,
  Input,
  IconButton,
  Checkbox,
  SegmentedControl,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useWaapAspect,
  useUpdateWaapAspect,
  WAAP_AREAS,
  type WaapSiteConfig,
  type WaapIpRule,
  type WaapWebsiteInfo,
  type WaapOrigin,
  type WaapMultiDomains,
} from '@/api/waap';
import { errMessage } from '@/pages/services/waap/waap-util';
import { WebsiteProtectionCard } from './WebsiteProtectionCard';
import styles from './ConfigurationTab.module.css';

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'DELETE', 'OPTIONS', 'HEAD', 'PATCH'];

const PROTECTION_MODES = [
  { value: 'intercept', label: 'Block' },
  { value: 'monitor', label: 'Monitor' },
  { value: 'passthrough', label: 'By-pass' },
];

// Block-response (only meaningful when protection_mode === intercept).
const BLOCK_ACTIONS = [
  { value: 'reject', label: 'Block' },
  { value: 'redirect', label: 'Redirection' },
  { value: 'blank', label: 'Blank Page' },
];

const IP_MODES = [
  { value: 'all_ip', label: 'No By-pass' },
  { value: 'ip_white_list', label: 'By-pass IP List' },
  { value: 'ip_protection_list', label: 'By-pass all excluding' },
];

const IP_MODE_HINT: Record<string, string> = {
  all_ip: 'All requests go through threat detection.',
  ip_white_list: 'Requests from these IPs bypass detection; all others are detected.',
  ip_protection_list: 'Only these IPs are detected; all other requests bypass detection.',
};

// Rule "Type" — vendor tuple slot 4. Only IP/Subnet mask is exposed for now.
const KINDS = [{ value: 'mask', label: 'IP/Subnet mask (Prefix length)' }];

const NEW_IP_RULE: WaapIpRule = { ip: '', mask: '', description: '', kind: 'mask' };

// ---- IP-rule validation ----------------------------------------------------
// The vendor's /site validator answers an opaque 500 on a bad tuple (e.g. a
// prefix length > 32), so mirror the portal's constraints client-side: a valid
// IP plus a prefix length (0–32 / 0–128) or a dotted subnet mask.

const isIPv4 = (s: string): boolean => {
  const parts = s.split('.');
  if (parts.length !== 4) return false;
  return parts.every((p) => /^\d{1,3}$/.test(p) && Number(p) <= 255);
};

const isIPv6 = (s: string): boolean => {
  if (!s.includes(':')) return false;
  const halves = s.split('::');
  if (halves.length > 2) return false;
  const groups = halves.flatMap((h) => (h === '' ? [] : h.split(':')));
  if (halves.length === 1 && groups.length !== 8) return false;
  if (halves.length === 2 && groups.length > 7) return false;
  return groups.every((g) => /^[0-9a-fA-F]{1,4}$/.test(g));
};

// A dotted IPv4 subnet mask must have contiguous leading 1-bits (255.255.254.0 ok).
const isSubnetMask = (s: string): boolean => {
  if (!isIPv4(s)) return false;
  const bits = s.split('.').map(Number).map((n) => n.toString(2).padStart(8, '0')).join('');
  return !bits.includes('01');
};

const isPrefixLen = (s: string, max: number): boolean =>
  /^\d{1,3}$/.test(s) && Number(s) <= max;

/** Validation error for one whitelist rule ('' = valid). */
const ipRuleError = (r: WaapIpRule): string => {
  const ip = r.ip.trim();
  const mask = r.mask.trim();
  if (!ip) return 'IP is required.';
  const v4 = isIPv4(ip);
  if (!v4 && !isIPv6(ip)) return 'Invalid IP address.';
  if (!mask) return 'Subnet mask / prefix length is required.';
  if (v4 && !isPrefixLen(mask, 32) && !isSubnetMask(mask))
    return 'Must be a prefix length (0–32) or a subnet mask like 255.255.255.0.';
  if (!v4 && !isPrefixLen(mask, 128)) return 'Must be a prefix length (0–128).';
  return '';
};

const DEFAULTS: WaapSiteConfig = {
  protection_mode: 'intercept',
  invalid_action: 'reject',
  invalid_action_redirect_path: '',
  allowed_http_methods: [...HTTP_METHODS],
  ip_strategy: { type: 'all_ip', white_list: [], protection_list: [] },
};

/**
 * Configuration — the website-detail Configuration tab. Protection Mode, Allowed
 * HTTP Methods and the Website IP Whitelist, persisted via the private portal
 * `/site` endpoint (whole-object read-modify-write on the backend). Website is
 * fixed by the detail route (`:domainId`).
 */
export function ConfigurationTab() {
  const { domainId } = useParams<{ domainId: string }>();
  const { show } = useToast();

  const { data, isLoading } = useWaapAspect<WaapSiteConfig>(domainId, WAAP_AREAS.site);
  // Read-only basic info (domain/cname/origins/http2/redirect) from the
  // delivery-domain detail — the authoritative source for the CNAME.
  const { data: info } = useWaapAspect<WaapWebsiteInfo>(domainId, WAAP_AREAS.websiteInfo);
  // HTTP/2 + Redirect-HTTP-to-HTTPS are OWNED by the Origin aspect (edited/saved on
  // the Origin tab). Read them from there — not from the websiteInfo read-model,
  // which can lag and show a stale tick that disagrees with the Origin tab.
  const { data: origin } = useWaapAspect<WaapOrigin>(domainId, WAAP_AREAS.origin);
  const [form, setForm] = useState<WaapSiteConfig>(DEFAULTS);

  // Multi Domains (alternate domain names). Source of truth is the server list;
  // each add/edit/delete PUTs the whole array (the vendor stores it as one field).
  const md = useWaapAspect<WaapMultiDomains>(domainId, WAAP_AREAS.multiDomains);
  const domains = md.data?.domains ?? [];
  const [newDomain, setNewDomain] = useState('');
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const saveMd = useUpdateWaapAspect<WaapMultiDomains>(domainId ?? '', WAAP_AREAS.multiDomains, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Alternate domains saved' });
      setNewDomain('');
      setEditingIdx(null);
    },
    onError: (e) => show({ tone: 'danger', title: 'Save failed', description: errMessage(e) }),
  });

  // Format-only pre-check. The domain-scope rule — an alternate must be a
  // sub-domain of the website's base (registrable) domain, e.g. "vncdn.vn" — is
  // owned and enforced by the vendor, whose message surfaces on save. We do NOT
  // re-derive the base domain on the client: doing it correctly needs a public
  // suffix list (".com.vn" etc.), and the earlier "sub-domain of the primary FQDN"
  // rule wrongly rejected valid siblings the vendor accepts (e.g. "x.vncdn.vn" for
  // primary "test.ioffice.vncdn.vn").
  const primaryDomain = (info?.domain ?? '').trim().toLowerCase();
  const altError = (raw: string): string => {
    const v = raw.trim().toLowerCase();
    if (!v) return 'Required.';
    if (!/^[a-z0-9.-]+$/.test(v)) return 'Only 0-9, a-z, - and . are allowed.';
    return '';
  };
  const putDomains = (list: string[]) => saveMd.mutate({ domains: list });
  const addDomain = () => {
    const v = newDomain.trim();
    const e = altError(v);
    if (e) { show({ tone: 'danger', title: 'Invalid alternate domain', description: e }); return; }
    if (domains.includes(v)) { setNewDomain(''); return; }
    putDomains([...domains, v]);
  };
  const deleteDomain = (i: number) => putDomains(domains.filter((_, idx) => idx !== i));
  const startEditDomain = (i: number) => { setEditingIdx(i); setEditValue(domains[i]); };
  const saveEditDomain = () => {
    const v = editValue.trim();
    const e = altError(v);
    if (e) { show({ tone: 'danger', title: 'Invalid alternate domain', description: e }); return; }
    putDomains(domains.map((d, idx) => (idx === editingIdx ? v : d)));
  };
  useEffect(() => {
    if (data) {
      setForm({
        ...DEFAULTS,
        ...data,
        allowed_http_methods: data.allowed_http_methods?.length ? data.allowed_http_methods : DEFAULTS.allowed_http_methods,
        ip_strategy: {
          type: data.ip_strategy?.type || 'all_ip',
          white_list: data.ip_strategy?.white_list ?? [],
          protection_list: data.ip_strategy?.protection_list ?? [],
        },
      });
    }
  }, [data]);

  const update = useUpdateWaapAspect<WaapSiteConfig>(domainId ?? '', WAAP_AREAS.site, {
    onSuccess: () => show({ tone: 'success', title: 'Configuration saved' }),
    onError: (e) => show({ tone: 'danger', title: 'Save failed', description: errMessage(e) }),
  });

  const set = <K extends keyof WaapSiteConfig>(k: K, v: WaapSiteConfig[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const toggleMethod = (m: string) =>
    set(
      'allowed_http_methods',
      form.allowed_http_methods.includes(m)
        ? form.allowed_http_methods.filter((x) => x !== m)
        : [...form.allowed_http_methods, m],
    );

  // The active IP list is selected by the bypass mode.
  const ipMode = form.ip_strategy.type;
  const activeKey = ipMode === 'ip_protection_list' ? 'protection_list' : 'white_list';
  const activeRules = form.ip_strategy[activeKey];
  const setActiveRules = (rules: WaapIpRule[]) =>
    set('ip_strategy', { ...form.ip_strategy, [activeKey]: rules });
  const setIpRule = (i: number, patch: Partial<WaapIpRule>) =>
    setActiveRules(activeRules.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addIpRule = () => setActiveRules([...activeRules, { ...NEW_IP_RULE }]);
  const removeIpRule = (i: number) => setActiveRules(activeRules.filter((_, idx) => idx !== i));

  // Both persisted lists are validated (both round-trip to the vendor even when
  // inactive); errors show live once a row has any input, and all on Save.
  const [triedSave, setTriedSave] = useState(false);
  const rowTouched = (r: WaapIpRule) => !!(r.ip.trim() || r.mask.trim() || r.description.trim());
  const showRuleError = (r: WaapIpRule) => (triedSave || rowTouched(r)) ? ipRuleError(r) : '';
  const onSave = () => {
    const listLabel: Record<string, string> = {
      white_list: 'By-pass IP List',
      protection_list: 'By-pass all excluding',
    };
    for (const key of ['white_list', 'protection_list'] as const) {
      if (form.ip_strategy[key].some((r) => ipRuleError(r))) {
        setTriedSave(true);
        show({
          tone: 'danger',
          title: 'Invalid IP rule',
          description: `Fix the highlighted rules in "${listLabel[key]}" before saving.`,
        });
        return;
      }
    }
    update.mutate(form);
  };

  if (isLoading) return <Skeleton className={styles.skeleton} />;

  return (
    <div className={styles.stack}>
      {domainId && <WebsiteProtectionCard domainId={domainId} />}

      <Card>
        <CardHeader>
          <CardTitle>Website Basic Information</CardTitle>
        </CardHeader>
        <CardBody>
          <div className={styles.infoGrid}>
            <div>
              <div className={styles.label}>Domain</div>
              <div className={styles.value}>{info?.domain ?? '—'}</div>
              <div className={styles.labelSpaced}>CNAME</div>
              <div className={styles.mono}>{info?.cname || '—'}</div>
            </div>
            <div>
              <div className={styles.label}>Origins</div>
              {(info?.origins ?? []).length ? (
                info!.origins.map((o, i) => (
                  <div key={i} className={styles.mono}>{o.protocol}{o.origin}</div>
                ))
              ) : (
                <span className={styles.muted}>—</span>
              )}
            </div>
            <div>
              <div className={styles.label}>Other</div>
              <label className={styles.infoCheck}>
                <Checkbox checked={!!origin?.http2} disabled /> HTTP/2
              </label>
              <label className={styles.infoCheckLast}>
                <Checkbox checked={!!origin?.redirect_http_to_https} disabled /> Redirect HTTP to HTTPS
              </label>
            </div>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          actions={
            <Button variant="secondary" size="sm" startIcon={<Plus size={14} />} disabled={saveMd.isPending} onClick={addDomain}>
              Add Alternate Domain
            </Button>
          }
        >
          <CardTitle>Multi Domains</CardTitle>
          <CardDescription>
            Alternate domains share this website's configuration and cache policy. Each one's certificate must be
            configured separately, and must be a sub-domain of this website's base domain.
          </CardDescription>
        </CardHeader>
        <CardBody className={styles.bodyGrid}>
          {/* Add new alternate domain */}
          <Field label="Alternate Domain Name" required>
            {(p) => (
              <Input
                {...p}
                placeholder={primaryDomain ? `sub.${primaryDomain}` : 'sub.example.com'}
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addDomain();
                  }
                }}
              />
            )}
          </Field>
          {newDomain.trim() && altError(newDomain) && (
            <span className={styles.altError}>
              {altError(newDomain)}
            </span>
          )}

          {/* Existing alternate domains */}
          <div className={styles.altList}>
            <div className={styles.altHead}>
              <span>Alternate Domain Name</span>
              <span>Operation</span>
            </div>
            {domains.length === 0 ? (
              <EmptyState icon={<Globe size={20} />} title="No alternate domains yet" description="Add alternate domain names for this website." />
            ) : (
              domains.map((d, i) => (
                <div key={d} className={styles.altRow}>
                  {editingIdx === i ? (
                    <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} className={styles.editInput} />
                  ) : (
                    <span>{d}</span>
                  )}
                  <div className={styles.rowActions}>
                    {editingIdx === i ? (
                      <>
                        <Button variant="ghost" size="sm" disabled={saveMd.isPending} onClick={saveEditDomain}>Save</Button>
                        <Button variant="ghost" size="sm" onClick={() => setEditingIdx(null)}>Cancel</Button>
                      </>
                    ) : (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => startEditDomain(i)}>Edit</Button>
                        <Button variant="ghost" size="sm" disabled={saveMd.isPending} onClick={() => deleteDomain(i)}>Delete</Button>
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Protection Mode</CardTitle>
          <CardDescription>
            How invalid/malicious requests are handled. Block returns 400; Monitor logs only; By-pass skips detection.
          </CardDescription>
        </CardHeader>
        <CardBody className={styles.bodyGrid}>
          <SegmentedControl
            items={PROTECTION_MODES}
            value={form.protection_mode}
            onValueChange={(v) => set('protection_mode', v)}
            aria-label="Protection mode"
          />
          {form.protection_mode === 'intercept' && (
            <div className={styles.blockRow}>
              <Field label="Block response" description="Block: return a blocked-request response; Redirection: send to a path on this site; Blank Page: return an empty page.">
                {(p) => (
                  <Select value={form.invalid_action} onValueChange={(v) => set('invalid_action', v)}>
                    <SelectTrigger {...p} className={styles.blockSelect}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BLOCK_ACTIONS.map((a) => (
                        <SelectItem key={a.value} value={a.value}>
                          {a.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              {form.invalid_action === 'redirect' && (
                <Field label="Redirect path" description="Where blocked requests are sent.">
                  {(p) => (
                    <Input
                      {...p}
                      placeholder="/blocked"
                      value={form.invalid_action_redirect_path}
                      onChange={(e) => set('invalid_action_redirect_path', e.target.value)}
                      className={styles.redirectInput}
                    />
                  )}
                </Field>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Allowed HTTP Method</CardTitle>
          <CardDescription>Only the selected methods are allowed and protected.</CardDescription>
        </CardHeader>
        <CardBody>
          <div className={styles.methodsRow}>
            {HTTP_METHODS.map((m) => (
              <label key={m} className={styles.checkLabel}>
                <Checkbox checked={form.allowed_http_methods.includes(m)} onCheckedChange={() => toggleMethod(m)} />
                {m}
              </label>
            ))}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Website IP Whitelist</CardTitle>
          <CardDescription>Choose how client IPs are routed through threat detection.</CardDescription>
        </CardHeader>
        <CardBody className={styles.bodyGrid}>
          <SegmentedControl
            items={IP_MODES}
            value={ipMode}
            onValueChange={(v) => set('ip_strategy', { ...form.ip_strategy, type: v })}
            aria-label="IP whitelist mode"
          />
          <div className={styles.ipHint}>{IP_MODE_HINT[ipMode]}</div>

          {ipMode !== 'all_ip' && (
            <div className={styles.ipList}>
              {activeRules.length > 0 && (
                <div className={styles.ipRow}>
                  <span className={`${styles.colHead} ${styles.kindSelect}`}>Type</span>
                  <span className={`${styles.colHead} ${styles.grow}`}>IP / Start IP</span>
                  <span className={`${styles.colHead} ${styles.grow}`}>Subnet mask or prefix length</span>
                  <span className={`${styles.colHead} ${styles.grow}`}>Description</span>
                  <span />
                </div>
              )}
              {activeRules.length === 0 && <div className={styles.muted}>No IP rules.</div>}
              {activeRules.map((r, i) => (
                <div key={i}>
                  <div className={styles.ipRow}>
                    <Select value={r.kind || 'mask'} onValueChange={(v) => setIpRule(i, { kind: v })}>
                      <SelectTrigger className={styles.kindSelect}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {KINDS.map((k) => (
                          <SelectItem key={k.value} value={k.value}>
                            {k.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input placeholder="IP / Start IP" value={r.ip} onChange={(e) => setIpRule(i, { ip: e.target.value })} className={styles.grow} title="IPv4 or IPv6 address" />
                    <Input placeholder="Subnet mask / Prefix / End IP" value={r.mask} onChange={(e) => setIpRule(i, { mask: e.target.value })} className={styles.grow} title="Prefix length (0–32 for IPv4, 0–128 for IPv6) or a dotted subnet mask like 255.255.255.0" />
                    <Input placeholder="Description" value={r.description} onChange={(e) => setIpRule(i, { description: e.target.value })} className={styles.grow} />
                    <IconButton aria-label="Remove IP rule" variant="ghost" onClick={() => removeIpRule(i)}>
                      <Trash2 size={16} />
                    </IconButton>
                  </div>
                  {showRuleError(r) && <div className={styles.ipError}>{showRuleError(r)}</div>}
                </div>
              ))}
              <div>
                <Button variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={addIpRule}>
                  Add IP Rule
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      <div className={styles.footer}>
        <Button disabled={update.isPending} onClick={onSave}>
          {update.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
